import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import { AlertMuteButton } from '@/components/AlertMuteButton';
import { AlertUnmuteButton } from '@/components/AlertUnmuteButton';
import { AlertExcludeButton } from '@/components/AlertExcludeButton';
import { AlertRemoveExclusionButton } from '@/components/AlertRemoveExclusionButton';
import { AlertEditPolicyButton } from '@/components/AlertEditPolicyButton';
import type { GroupedAlert } from '@kirin/api-client/contracts/alerts';
import {
  AlertsSortField,
  outcomeOptions,
} from '@kirin/api-client/contracts/alerts';
import {
  formatTypeFilterLabel,
  formatTitleFilterLabel,
} from '@kirin/api-client/helpers/alerts';
import { USER_ALERTS_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/alerts';
import { severityOptionsDescending } from '@kirin/api-client/contracts/shared/severity';
import {
  USER_ALERTS_COLUMN_URL_KEYS,
  USER_ALERTS_TITLE_FILTER_URL_KEY,
} from './constants';
import {
  TypeCell,
  SeverityCell,
  FilePathCell,
  IssuesCell,
  ScoreCell,
  LastSeenCell,
  FirstSeenCell,
  ClientTypesCell,
  OutcomeCell,
  TITLE_COLUMN_WIDTH,
} from '@/components/DataTable/alerts/CellRenderers';
import { getTimestampColumnWidth } from '@/components/shared/timestampColumnWidth';
import type { TimestampFormat } from '@/contexts/TimestampFormatContext';
import {
  SeverityFilterOption,
  FilePathFilterOption,
  ClientTypeFilterOption,
} from '@/components/DataTable/alerts/FilterComponents';
import { getPlatformLabel } from '@/components/shared/platform/platform-utils';
import { queryKeys } from '@/hooks/api/queryKeys';
import {
  fetchUserAlertTypeOptions,
  fetchUserAlertTitleOptions,
  fetchUserAlertFileOptions,
  fetchUserAlertClientTypeOptions,
} from './api';

const ValuelessOperators = [
  { value: FilterOperators.IS_EMPTY, label: 'Is Empty' },
  { value: FilterOperators.IS_NOT_EMPTY, label: 'Is Not Empty' },
];

const DateOperators = [
  { value: FilterOperators.GREATER_THAN, label: 'Is After' },
  { value: FilterOperators.LESS_THAN, label: 'Is Before' },
  { value: FilterOperators.BETWEEN, label: 'Is Between' },
  ...ValuelessOperators,
];

/**
 * Column definitions for the user (dashboard) alerts DataTable.
 * No Users column — this view is always scoped to the authenticated user.
 */
export const createUserAlertsColumns = (
  includeIdeColumn: boolean = false,
  revampEnabled: boolean = false,
  timestampFormat: TimestampFormat = 'relative'
): ColumnConfig<GroupedAlert>[] => {
  const timestampColumnWidth = getTimestampColumnWidth(timestampFormat);
  const clientTypesColumn: ColumnConfig<GroupedAlert> = {
    id: 'clientTypes',
    header: 'IDE/Platforms',
    accessor: 'clientTypes',
    renderCell: ({ row }) => <ClientTypesCell row={row} />,
    width: 200,
    sortable: true,
    sortField: AlertsSortField.ClientTypes,
    sortKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.ClientTypes],
    filterable: true,
    dataType: ColumnDataTypes.ENUM,
    filterOperators: [
      { value: FilterOperators.INCLUDES, label: 'Includes' },
      { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ...ValuelessOperators,
    ],
    statePersistence: {
      urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.ClientTypes],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: params =>
        queryKeys.alerts.filterOptions('clientTypes', params),
      queryFn: params => fetchUserAlertClientTypeOptions(params),
      queryConfig: {
        staleTime: 2 * 60 * 1000,
      },
      labelFormatter: getPlatformLabel,
      render: (option, isSelected) => (
        <ClientTypeFilterOption
          clientType={option.value}
          isSelected={isSelected}
        />
      ),
    },
  };

  // KIR-3769: Title/Type column — see the org table's `titleOrTypeColumn` for
  // the full rationale. revamp ON = composite `title` filter (client-side
  // label search, distinct `ti` url key); revamp OFF = byte-identical Type
  // column (server-side search, `t` url key).
  const titleOrTypeColumn: ColumnConfig<GroupedAlert> = revampEnabled
    ? {
        id: USER_ALERTS_FILTERABLE_FIELDS.TITLE,
        header: 'Title',
        accessor: 'type',
        renderCell: ({ row }) => (
          <TypeCell row={row} revampEnabled={revampEnabled} />
        ),
        width: TITLE_COLUMN_WIDTH,
        sortable: true,
        // Sort still keys off the `type` slug (unchanged); `t` sort key kept.
        sortField: 'type',
        sortKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Type],
        filterable: true,
        dataType: ColumnDataTypes.ENUM,
        filterOperators: [
          { value: FilterOperators.INCLUDES, label: 'Includes' },
          { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ...ValuelessOperators,
        ],
        statePersistence: {
          urlKey: USER_ALERTS_TITLE_FILTER_URL_KEY,
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params =>
            queryKeys.alerts.filterOptions('title', params),
          queryFn: params => fetchUserAlertTitleOptions(params),
          queryConfig: {
            staleTime: 2 * 60 * 1000,
          },
          clientSideSearch: true,
          labelFormatter: formatTitleFilterLabel,
        },
      }
    : {
        id: 'type',
        header: 'Type',
        accessor: 'type',
        renderCell: ({ row }) => (
          <TypeCell row={row} revampEnabled={revampEnabled} />
        ),
        // KIR-2941: shared with the org alerts table via the constant.
        width: TITLE_COLUMN_WIDTH,
        sortable: true,
        sortField: 'type',
        sortKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Type],
        filterable: true,
        dataType: ColumnDataTypes.STRING,
        filterOperators: [
          { value: FilterOperators.INCLUDES, label: 'Includes' },
          { value: FilterOperators.EXCLUDES, label: 'Excludes' },
          ...ValuelessOperators,
        ],
        statePersistence: {
          urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Type],
        },
        filterOptions: {
          type: FilterOptionTypes.DYNAMIC,
          getQueryKey: params => queryKeys.alerts.filterOptions('type', params),
          queryFn: params => fetchUserAlertTypeOptions(params),
          queryConfig: {
            staleTime: 2 * 60 * 1000,
          },
          // KIR-2381: drives dropdown labels off the same registry as the
          // cell renderer so option labels match what users see in the table.
          labelFormatter: (type: string) =>
            formatTypeFilterLabel(type, revampEnabled),
        },
      };

  // KIR-3769 back-compat: mirrors the org table's `hiddenTypeCompatColumn`.
  // Revamp-on the visible column filters on `title` (url key `ti`); this hidden
  // column keeps the legacy `type` filter (url key `t`) alive so old "View
  // Details" deep links still resolve instead of being dropped, without
  // rendering a second visible column.
  const hiddenTypeCompatColumn: ColumnConfig<GroupedAlert> = {
    id: USER_ALERTS_FILTERABLE_FIELDS.TYPE,
    header: 'Type',
    accessor: 'type',
    hidden: true,
    filterable: true,
    dataType: ColumnDataTypes.STRING,
    filterOperators: [
      { value: FilterOperators.INCLUDES, label: 'Includes' },
      { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ...ValuelessOperators,
    ],
    statePersistence: {
      urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Type],
    },
    filterOptions: {
      type: FilterOptionTypes.DYNAMIC,
      getQueryKey: params => queryKeys.alerts.filterOptions('type', params),
      queryFn: params => fetchUserAlertTypeOptions(params),
      queryConfig: {
        staleTime: 2 * 60 * 1000,
      },
      labelFormatter: (type: string) =>
        formatTypeFilterLabel(type, revampEnabled),
    },
  };

  return [
    {
      id: 'lastSeenAt',
      header: 'Last Seen',
      accessor: 'lastSeenAt',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      // Widened so the absolute UTC string fits without truncation when the
      // Timestamp Format setting is "absolute" (KIR-1285).
      width: timestampColumnWidth,
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.LastSeenAt],
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.LastSeenAt],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
    titleOrTypeColumn,
    ...(revampEnabled ? [hiddenTypeCompatColumn] : []),
    {
      id: 'severity',
      header: 'Severity',
      accessor: 'severity',
      renderCell: ({ row }) => <SeverityCell row={row} />,
      width: 120,
      sortable: true,
      sortField: 'severity',
      sortKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Severity],
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Severity],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: severityOptionsDescending,
        render: (option, isSelected) => (
          <SeverityFilterOption
            severity={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: 'outcome',
      header: 'Outcome',
      accessor: 'outcome',
      renderCell: ({ row }) => <OutcomeCell row={row} />,
      width: 130,
      sortable: true,
      sortField: AlertsSortField.Outcome,
      sortKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Outcome],
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Outcome],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: outcomeOptions.map(o => ({ value: o.value, label: o.label })),
      },
    },
    {
      id: 'filePath',
      header: 'File',
      accessor: 'filePath',
      renderCell: ({ row }) => <FilePathCell row={row} />,
      width: 320,
      // File column is display-only: the cell shows a resolved label
      // (real path from `location`, or a friendly sentinel description),
      // which server-side sort on the stored `filePath` cannot match. KIR-3154.
      sortable: false,
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.FilePath],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        getQueryKey: params =>
          queryKeys.alerts.filterOptions('filePath', {
            search: params.search,
            limit: params.limit,
          }),
        queryFn: params => fetchUserAlertFileOptions(params),
        queryConfig: {
          staleTime: 2 * 60 * 1000,
        },
        dropdown: {
          width: 360,
        },
        pagination: {
          mode: 'offset',
          limit: 10,
        },
        render: (option, isSelected) => (
          <FilePathFilterOption
            filePath={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    ...(includeIdeColumn ? [clientTypesColumn] : []),
    {
      id: 'occurrenceCount',
      header: 'Issues',
      accessor: 'occurrenceCount',
      renderCell: ({ row }) => <IssuesCell row={row} />,
      width: 90,
      sortable: true,
      sortField: 'occurrenceCount',
      sortKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.OccurrenceCount],
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.OccurrenceCount],
      },
      range: {
        min: 1,
        max: 100,
        step: 1,
      },
    },
    {
      id: 'score',
      header: 'Score',
      accessor: 'score',
      renderCell: ({ row }) => <ScoreCell row={row} />,
      width: 90,
      sortable: true,
      sortField: 'score',
      sortKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Score],
      filterable: true,
      dataType: ColumnDataTypes.NUMBER,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        ...ValuelessOperators,
      ],
      statePersistence: {
        urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.Score],
      },
      range: {
        min: 0,
        max: 100,
        step: 1,
      },
    },
    {
      id: 'firstSeenAt',
      header: 'First Seen',
      accessor: 'firstSeenAt',
      renderCell: ({ row }) => <FirstSeenCell row={row} />,
      // Widened so the absolute UTC string fits without truncation when the
      // Timestamp Format setting is "absolute" (KIR-1285).
      width: timestampColumnWidth,
      sortable: true,
      sortField: 'firstSeenAt',
      sortKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.FirstSeenAt],
      filterable: true,
      dataType: ColumnDataTypes.DATE,
      filterOperators: DateOperators,
      statePersistence: {
        urlKey: USER_ALERTS_COLUMN_URL_KEYS[AlertsSortField.FirstSeenAt],
      },
      filterOptions: {
        type: FilterOptionTypes.STATIC,
        options: [],
      },
    },
  ];
};

/**
 * Factory for the Actions column on the dashboard (account-scope) alerts
 * table. Account scope has no RBAC gate — any authenticated user may mute
 * or exclude their own alerts. Consumed as an additive append to the
 * columns array only when EITHER `alert_muting` or `alert_ignore` is on.
 * Each button is itself flag-gated, so the cell composes naturally across
 * the matrix of feature-flag states.
 */
export function createUserAlertsActionsColumn(): ColumnConfig<GroupedAlert> {
  return {
    id: 'actions',
    header: 'Actions',
    // Mirrors `createOrganizationAlertsActionsColumn`; see that comment
    // for the layout math (Mute + Exclude + Edit Policy + gap-2 + padding).
    width: 416,
    sortable: false,
    filterable: false,
    renderCell: ({ row }) => {
      const scope = { kind: 'account' as const };
      const analyticsContext = { severity: row.severity, type: row.type };
      return (
        <div
          className="flex items-center justify-start gap-2 pr-2"
          onClick={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
        >
          {row.mute?.muted ? (
            <AlertUnmuteButton
              scope={scope}
              alertKey={row.alertKey}
              mute={row.mute}
              analyticsContext={analyticsContext}
            />
          ) : (
            <AlertMuteButton
              scope={scope}
              alertKey={row.alertKey}
              analyticsContext={analyticsContext}
            />
          )}
          {row.exclude?.excluded ? (
            <AlertRemoveExclusionButton
              scope={scope}
              alertKey={row.alertKey}
              exclude={row.exclude}
              analyticsContext={analyticsContext}
            />
          ) : (
            <AlertExcludeButton
              scope={scope}
              alertKey={row.alertKey}
              analyticsContext={analyticsContext}
              findingType={row.type}
            />
          )}
          <AlertEditPolicyButton
            scope={{ kind: 'account' }}
            policyKey={row.policyKey}
            subPolicyKey={row.subPolicyKey}
            alertKey={row.alertKey}
          />
        </div>
      );
    },
  };
}
